/**
 * Generic Loading Component
 *
 * Reusable loading screen with customizable title and subtitle.
 * Resolves the current machine state to display contextually appropriate copy.
 */

"use client";

import { Loader2 } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useInsuranceConnectorState } from "../insurance-connector-provider";

interface LoadingContent {
  title: string;
  subtitle: string;
}

/**
 * Extracts the leaf state name from a potentially nested XState state value.
 * e.g. { full: "checkingReturnUser" } → "checkingReturnUser"
 *      { minimized: "loadingInsurance" } → "loadingInsurance"
 *      "idle" → "idle"
 */
function getLeafStateName(stateValue: unknown): string {
  if (typeof stateValue === "string") return stateValue;
  if (typeof stateValue === "object" && stateValue !== null) {
    const values = Object.values(stateValue);
    if (values.length > 0) return getLeafStateName(values[0]);
  }
  return "";
}

export function LoadingScreen() {
  const t = useTranslations("InsuranceConnector");
  const state = useInsuranceConnectorState();
  const stateName = getLeafStateName(state.value);

  const TRANSLATED_CONTENT: Record<string, LoadingContent> = {
    checkingReturnUser: {
      title: t("CheckingInfo"),
      subtitle: t("LookingForAccount"),
    },
    sendingSMS: {
      title: t("SendingVerificationCode"),
      subtitle: t("MayTakeAMoment"),
    },
    verifyingSMS: { title: t("VerifyingCode"), subtitle: t("PleaseWait") },
    disambiguating: { title: t("CheckingInfo"), subtitle: t("PleaseWait") },
    sendingDisambiguate: {
      title: t("CheckingInfo"),
      subtitle: t("PleaseWait"),
    },
    loadingInsurance: {
      title: t("VerifyingInsurance"),
      subtitle: t("MayTakeFewMoments"),
    },
  };
  const content = TRANSLATED_CONTENT[stateName] || {
    title: t("Loading"),
    subtitle: t("PleaseWait"),
  };

  return (
    <div className="p-6 rounded-3xl h-full">
      <div className="flex flex-col items-center justify-center py-12 h-full">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold">{content.title}</h2>
          <p className="text-muted-foreground">{content.subtitle}</p>
        </div>
      </div>
    </div>
  );
}
